


















































import Vue from "vue";
import Toasted from "vue-toasted";
import ChargeModel from "@/models/ChargeModel";
import AuctionItemModel from "@/models/AuctionItemModel";
import AuctionCloseModel from "@/models/AuctionCloseModel";
import AuctionAdminModel from "@/models/AuctionAdminModel";
import BidModel from "@/models/BidModel";
import filters from "@/global/filters";

Vue.use(Toasted, {
	position: "bottom-left",
	fullWidth: true,
	duration: 8000,
});

export default Vue.extend({
	data() {
		return {
			isLoading: true as boolean,
			isClosing: false as boolean,
			isPosting: false as boolean,
			auctionClosed: false as boolean,
			auctionItems: [] as AuctionItemModel[],
		};
	},

	async mounted() {
		try {
			// Reflect the live auction status so the right button shows.
			try {
				let state = await BidModel.state();
				this.auctionClosed = state.auctionStatus === "closed";
			} catch (e) {
				// Non-fatal — defaults to open.
			}

			let auctionItems = await AuctionItemModel.fetchBy();

			if (Array.isArray(auctionItems)) {
				// The sheet's "Amount" column is the starting bid, not the winner.
				// With mobile bidding off, blank the winner fields so organizers
				// key them in by hand; charged items are refilled from the
				// Charges sheet by refreshChargedStatus below.
				if (!this.$store.getters.isMobileBiddingEnabled) {
					auctionItems.forEach((item) => {
						item.bidNumber = null as any;
						item.amount = null as any;
					});
				}

				this.auctionItems = auctionItems;
				await this.refreshChargedStatus();
			}

			// Prefill any not-yet-charged item with its live winning bid, but
			// only when mobile bidding is on. With it disabled there is no bid
			// database to pull from and organizers key in winners by hand, so
			// the fields must start empty.
			if (this.$store.getters.isMobileBiddingEnabled) await this.loadLiveWinners();

			this.isLoading = false;
		} catch (error) {
			console.error(error);
			this.$toasted.show("Error fetching auction winners", { type: "error" });
		}
	},

	methods: {
		routeSecret(): string | undefined {
			return this.$route.query.secret as string | undefined;
		},

		// Sync each item's charged state from the Charges sheet (by sku).
		async refreshChargedStatus() {
			for (let item of this.auctionItems) {
				let charges = await ChargeModel.fetchBySku(`auction-item-${item.itemNumber}`);

				if (Array.isArray(charges) && charges.length) {
					let charge = charges[0];
					item.isCharged = true;
					item.bidNumber = charge.bidNumber;
					item.amount = charge.amount;
				}

				item.isLoading = false;
			}
		},

		// Merge live winning bids from the auction backend into any item that
		// hasn't been reconciled into a charge yet, so the admin only has to
		// confirm rather than key in bidder/amount by hand.
		applyWinners(winners: { itemNumber: number; bidNumber: number; amount: number }[]) {
			for (let winner of winners) {
				let item = this.auctionItems.find((i) => i.itemNumber === winner.itemNumber);
				if (item && !item.isCharged) {
					item.bidNumber = winner.bidNumber;
					item.amount = winner.amount;
				}
			}
		},

		async loadLiveWinners() {
			try {
				let winners = await AuctionCloseModel.winners(this.routeSecret());
				this.applyWinners(winners);
			} catch (error) {
				// Non-fatal: admin can still enter winners manually.
				console.error("Unable to load live winning bids", error);
			}
		},

		async closeAuction() {
			if (!window.confirm("Close bidding for all items? Winning bids will be posted to the Charges sheet.")) return;

			this.isClosing = true;

			try {
				let result = await AuctionCloseModel.close(this.routeSecret());
				this.auctionClosed = !!result.closed;
				this.applyWinners(result.winners);
				// Server auto-posts winners to Charges on close — reflect it here.
				await this.refreshChargedStatus();

				let c = result.charges;
				let msg = "Auction closed.";
				if (c) {
					msg += ` Posted ${c.created} winner(s) to Charges`;
					if (c.existed) msg += `, ${c.existed} already posted`;
					if (c.errored) msg += `, ${c.errored} failed`;
					msg += ".";
				}
				this.$toasted.show(msg, { type: c && c.errored ? "error" : "success" });
			} catch (error) {
				console.error(error);
				this.$toasted.show("Unable to close the auction. Check the admin secret.", { type: "error" });
			}

			this.isClosing = false;
		},

		async reopenAuction() {
			this.isClosing = true;

			try {
				await AuctionAdminModel.setStatus("open", this.routeSecret());
				this.auctionClosed = false;
				this.$toasted.show("Bidding reopened.", { type: "success" });
			} catch (error) {
				console.error(error);
				this.$toasted.show("Unable to reopen bidding.", { type: "error" });
			}

			this.isClosing = false;
		},

		// Idempotently post one item's winning bid to the Charges sheet.
		// Returns "created" | "exists" | "skipped" | "error".
		async chargeItem(item: AuctionItemModel): Promise<string> {
			if (item.isCharged) return "skipped";
			if (!item.bidNumber || !item.amount || item.amount <= 0) return "skipped";

			item.isSubmitting = true;

			try {
				// Guard against double-posting if a charge already exists for this item.
				let existing = await ChargeModel.fetchBySku(`auction-item-${item.itemNumber}`);
				if (Array.isArray(existing) && existing.length) {
					item.bidNumber = existing[0].bidNumber;
					item.amount = existing[0].amount;
					item.isCharged = true;
					return "exists";
				}

				let charge = new ChargeModel({
					amount: item.amount,
					bidNumber: item.bidNumber,
					name: `#${item.itemNumber}: ${item.name}`,
					type: `Auction Item`,
					sku: `auction-item-${item.itemNumber}`,
				});

				await charge.create();
				item.isCharged = true;
				return "created";
			} catch (error) {
				console.error(`Failed to post charge for item ${item.itemNumber}`, error);
				return "error";
			} finally {
				item.isSubmitting = false;
			}
		},

		async submitCharge(item: AuctionItemModel) {
			let result = await this.chargeItem(item);
			if (result === "error") {
				this.$toasted.show(`Could not post item #${item.itemNumber}.`, { type: "error" });
			} else if (result === "exists") {
				this.$toasted.show(`Item #${item.itemNumber} was already charged.`, { type: "info" });
			}
		},

		// Post every pending winner (uncharged item with a bidder + amount) to Charges.
		async submitAllWinners() {
			let pending = this.auctionItems.filter((i) => !i.isCharged && !!i.bidNumber && i.amount > 0);

			if (!pending.length) {
				this.$toasted.show("No pending winners to post.", { type: "info" });
				return;
			}

			if (!window.confirm(`Post ${pending.length} winning bid(s) to the Charges sheet?`)) return;

			this.isPosting = true;

			let created = 0;
			let existed = 0;
			let errored = 0;

			for (let item of pending) {
				let result = await this.chargeItem(item);
				if (result === "created") created++;
				else if (result === "exists") existed++;
				else if (result === "error") errored++;
			}

			this.isPosting = false;

			let summary = `Posted ${created} charge(s) to the sheet`;
			if (existed) summary += `, ${existed} already existed`;
			if (errored) summary += `, ${errored} failed`;
			this.$toasted.show(summary, { type: errored ? "error" : "success" });
		},

		formatCurrency(value: number) {
			return filters.currency(value);
		},
	},

	computed: {
		total(): number {
			let total = 0;

			for (let item of this.auctionItems) {
				if (item.isCharged) {
					total += item.amount;
				}
			}

			return total;
		},

		pendingWinnerCount(): number {
			return this.auctionItems.filter((i) => !i.isCharged && !!i.bidNumber && i.amount > 0).length;
		},
	},

	watch: {},
});
